// §4.1's schedule half of the workbench (P3-I-2, D-398, D-399, D-401): the
// schedule tile with the published version, when and how it was published, its
// late orders and overloads and a "New plan waiting" badge naming the tests
// that held the draft; the last solve with its trigger, status and time and a
// queued request's due time; Solve for a holder of `planning.schedule.publish`;
// the count of consecutive failed solves in red; and the overload tile, open
// overloads of the published schedule by work centre, each linked to its lane
// on the board. The workbench mounts it only for a holder of
// `planning.schedule.read`.

import { useEffect } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { StatStrip, StatTile } from "../../../../components/stat-strip";
import { EmptyState, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useCalendarWorkCenters } from "../hooks/calendar-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { useScheduleHeads, useScheduleOverloads, useScheduleRuns } from "../hooks/schedule-queries";
import { workCenterLabel } from "../lib/calendar-lib";
import { zonedStamp } from "../lib/schedule-lib";
import { usePlanningFacility } from "../planning-layout";
import {
  SolveErrorLine,
  SolveStatusChip,
  SolveTriggerChip,
  useSolveControl,
} from "./schedule-bits";
import { shortId } from "../../../../lib/short-id";

export function ScheduleWorkbenchSection() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const canPublish = perms.has("planning.schedule.publish");
  const zone = facility.timezone;
  const heads = useScheduleHeads(facility.id);
  const runs = useScheduleRuns(facility.id, undefined);
  const published = heads.data?.published ?? null;
  const draft = heads.data?.draft ?? null;
  const overloads = useScheduleOverloads(facility.id, published?.id);
  const workCenters = useCalendarWorkCenters(facility.id);
  const solve = useSolveControl(facility.id, {
    canSolve: canPublish,
    testIdPrefix: "planning-workbench-solve",
  });

  const authError = [heads.error, runs.error].find((e) => e instanceof UnauthorizedError);
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const time = (iso: string | null) => zonedStamp(iso, i18n.language, zone);
  const sectionHead = (
    <div className="planning-mrp-head">
      <h2 id="planning-schedule-h">{t("planning.schedule.workbench.title")}</h2>
      <div className="planning-actions">{solve.button}</div>
    </div>
  );

  const readError = heads.error ?? runs.error;
  if (readError !== null && authError === undefined) {
    return (
      <section
        className="planning-mrp"
        aria-labelledby="planning-schedule-h"
        data-testid="planning-schedule-section"
      >
        {sectionHead}
        <ErrorState
          title={t("planning.schedule.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => {
            void heads.refetch();
            void runs.refetch();
          }}
          requestId={requestIdOf(readError)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const list = runs.data;
  const last = list?.items[0];
  const pending = list?.pending_request ?? null;
  const failures = list?.consecutive_failures ?? 0;
  const loading = heads.isPending || runs.isPending;
  const nothing =
    !loading &&
    published === null &&
    draft === null &&
    (list?.items.length ?? 0) === 0 &&
    pending === null;
  const open = overloads.data ?? [];
  const byWc = new Map<string, number>();
  for (const o of open) byWc.set(o.work_center_id, (byWc.get(o.work_center_id) ?? 0) + 1);
  const wcName = (id: string) => {
    const wc = workCenters.data?.find((w) => w.id === id);
    return wc === undefined ? shortId(id) : workCenterLabel(wc, i18n.language);
  };
  const automatic = published !== null && published.published_by === null;
  const held = draft?.auto_publish?.held_by ?? [];

  return (
    <section
      className="planning-mrp"
      aria-labelledby="planning-schedule-h"
      data-testid="planning-schedule-section"
    >
      {sectionHead}
      {solve.banner}
      {failures > 0 ? (
        <Banner kind="bad" data-testid="planning-workbench-solve-failures">
          {t("planning.schedule.failures", { count: failures })}
        </Banner>
      ) : null}
      {nothing ? (
        <div data-testid="planning-workbench-schedule-empty">
          <EmptyState
            title={t("planning.schedule.emptyTitle")}
            body={t("planning.schedule.emptyBody")}
          />
        </div>
      ) : (
        <StatStrip data-testid="planning-schedule-tiles">
          <StatTile
            label={t("planning.schedule.workbench.schedule")}
            loading={heads.isPending}
            data-testid="planning-tile-schedule"
            value={
              published === null ? (
                "–"
              ) : (
                <Link
                  to="/planning/schedule"
                  className="planning-mps-tile-link"
                  data-schedule-id={published.id}
                  data-version={published.version}
                >
                  {t("planning.schedule.version", { version: published.version })}
                </Link>
              )
            }
            sub={
              <span className="planning-tile-counts">
                {published === null ? (
                  <span>{t("planning.schedule.workbench.noPublished")}</span>
                ) : (
                  <>
                    <span>{time(published.published_at)}</span>
                    <span data-testid="planning-tile-schedule-auto" data-auto={String(automatic)}>
                      {automatic
                        ? t("planning.schedule.publishedAuto")
                        : t("planning.schedule.publishedByPerson")}
                    </span>
                    <span
                      data-testid="planning-tile-schedule-late"
                      data-count={published.kpis.late_orders}
                    >
                      {t("planning.schedule.workbench.lateOrders", {
                        count: published.kpis.late_orders,
                      })}
                    </span>
                    <span
                      data-testid="planning-tile-schedule-overloads"
                      data-count={published.kpis.overloads}
                    >
                      {t("planning.schedule.workbench.overloads", {
                        count: published.kpis.overloads,
                      })}
                    </span>
                  </>
                )}
                {draft !== null ? (
                  <Link
                    to="/planning/schedule"
                    data-testid="planning-tile-schedule-waiting"
                    data-schedule-id={draft.id}
                    data-held-by={held.join(" ")}
                  >
                    <StatusChip variant="warn">{t("planning.schedule.draft.waiting")}</StatusChip>
                    {held.length > 0
                      ? ` ${held.map((h) => t(`planning.schedule.heldShort.${h}`)).join(", ")}`
                      : null}
                  </Link>
                ) : null}
              </span>
            }
          />
          <StatTile
            label={t("planning.schedule.workbench.lastSolve")}
            loading={runs.isPending}
            data-testid="planning-tile-solve"
            value={
              last === undefined ? (
                "–"
              ) : (
                <SolveStatusChip status={last.status} testId="planning-tile-solve-status" />
              )
            }
            sub={
              <span className="planning-tile-counts">
                {last === undefined ? (
                  <span>{t("planning.schedule.workbench.noSolve")}</span>
                ) : (
                  <>
                    <SolveTriggerChip trigger={last.trigger} />
                    <span>{time(last.finished_at ?? last.started_at)}</span>
                    {last.status === "failed" ? <SolveErrorLine run={last} /> : null}
                  </>
                )}
                {pending !== null ? (
                  <span data-testid="planning-tile-solve-queued" data-due-at={pending.due_at}>
                    {t("planning.schedule.workbench.queued", { time: time(pending.due_at) })}
                  </span>
                ) : null}
              </span>
            }
          />
          <StatTile
            label={t("planning.schedule.workbench.overloadTile")}
            loading={published !== null && overloads.isPending}
            data-testid="planning-tile-overloads"
            value={
              published === null ? (
                "–"
              ) : (
                <Link to="/planning/schedule" className="planning-mps-tile-link">
                  {open.length}
                </Link>
              )
            }
            sub={
              open.length === 0 ? (
                t("planning.schedule.workbench.noOverloads")
              ) : (
                <span className="planning-tile-counts" data-testid="planning-tile-overloads-by-wc">
                  {[...byWc.entries()].map(([wc, n]) => (
                    <Link
                      key={wc}
                      to="/planning/schedule"
                      search={{ wc }}
                      data-work-center-id={wc}
                      data-count={n}
                    >
                      {wcName(wc)} {n}
                    </Link>
                  ))}
                </span>
              )
            }
          />
        </StatStrip>
      )}
    </section>
  );
}
